import { MenuIcons } from '../components/CardMenu';

// What the "⋯" menu on a vendor card actually does.
//
// Modelled on Trello's card menu, but two of Trello's items don't map cleanly
// onto a technician and are handled differently — see the notes on each.

// A link straight to one vendor. The app is a single page with no router, so
// this is a hash the app reads on load (see App.js) rather than a real route.
export const vendorLink = (vendor) =>
  `${window.location.origin}/#/vendor/${vendor._id}`;

// Everything on the card as plain text, ready to paste into a text message,
// an email, or a note.
const vendorAsText = (v) => {
  const p = v.profile || {};
  const lines = [
    v.businessName,
    v.phone || v.userId?.phone,
    v.email || v.userId?.email,
    p.address,
    p.expertise?.length ? `Trades: ${p.expertise.join(', ')}` : null,
    v.compliance
      ? (v.compliance.canBePaid ? 'Paperwork current' : 'Paperwork NOT current — payments blocked')
      : null,
  ];
  return lines.filter(Boolean).join('\n');
};

// navigator.clipboard needs a secure context (https, or localhost). The live
// site is https so this is the normal path; the fallback covers anything else.
async function copyText(text) {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch { /* fall through to the textarea trick */ }
  try {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.style.position = 'fixed';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(ta);
    return ok;
  } catch {
    return false;
  }
}

/**
 * Builds the menu for one vendor card.
 *
 * @param {Object} ctx
 *   onOpen      — open the vendor's full profile
 *   onEdit      — open the edit form (name, phone, address, rates…)
 *   onWatch     — toggle the eye (persisted; see PATCH /api/vendors/:id/watch)
 *   onDelete    — hand off to the existing "Confirm Deletion" dialog
 *   showToast   — feedback, since copying gives no visible signal otherwise
 */
// A technician's number as a dialler wants it: digits only, with a leading +1
// added for a bare ten-digit US number so an international phone does not
// refuse it. Namecheap-formatted, hand-typed, pasted from a text — they all
// arrive differently and all have to work.
export const dialable = (v) => {
  const raw = String(v?.phone || v?.userId?.phone || '').trim();
  if (!raw) return '';
  if (raw.startsWith('+')) return '+' + raw.replace(/\D/g, '');
  const d = raw.replace(/\D/g, '');
  if (d.length === 10) return `+1${d}`;
  if (d.length === 11 && d.startsWith('1')) return `+${d}`;
  return d ? `+${d}` : '';
};

// The href that opens a new text message to them.
//
// iOS wants sms:+1555…&body=, everything else wants ?body=. Getting it wrong
// opens the messaging app with an empty message, which is a nuisance rather
// than a failure, so the sniff is worth it and nothing depends on it.
export const smsHref = (v, body = '') => {
  const to = dialable(v);
  if (!to) return '';
  if (!body) return `sms:${to}`;
  const sep = /iPhone|iPad|iPod|Macintosh/.test(navigator.userAgent) ? '&' : '?';
  return `sms:${to}${sep}body=${encodeURIComponent(body)}`;
};

export const vendorMenuItems = ({ onOpen, onEdit, onWatch, onDelete, onMove, showToast }) => (v, column) => [
  {
    key: 'open',
    label: 'Open card',
    icon: MenuIcons.link,
    onSelect: () => onOpen(v),
  },
  {
    // A tech fills the application in on a phone, so the details arrive with
    // typos. Fixing them from the card is quicker than opening the profile.
    key: 'edit',
    label: 'Edit details',
    icon: MenuIcons.pencil,
    hidden: !onEdit,
    onSelect: () => onEdit(v),
  },
  {
    // Trello's first menu item, and on a phone it's the only reliable way to
    // move a card: dragging inside a side-scrolling board fights the scroll
    // gesture. Dragging still works with a mouse — this is the path that
    // always works.
    key: 'move',
    label: 'Move card',
    icon: MenuIcons.move,
    hidden: !onMove,
    onSelect: () => onMove(v, column),
  },
  {
    // Texting a technician is the single most common thing Julian does with a
    // card, and until now it meant leaving the app to find the number.
    //
    // This opens the phone's own messaging app. It goes out from HIS number
    // rather than the QPS line, and it is not logged in the app — that is the
    // Twilio relay, which is a separate thing and currently switched off. What
    // this does is work right now, on a phone and on a Mac with Messages, with
    // no setup at all.
    key: 'text',
    label: 'Text them',
    icon: MenuIcons.text,
    hidden: !dialable(v),
    href: smsHref(v),
  },
  {
    key: 'call',
    label: 'Call them',
    icon: MenuIcons.phone,
    hidden: !dialable(v),
    href: `tel:${dialable(v)}`,
  },
  {
    // Trello's "Copy card" duplicates the card. Duplicating a technician would
    // create a second record for a real person — with the same email, which
    // their login is keyed on — so this copies the card's details to the
    // clipboard instead. That's the thing you actually want when you're
    // passing a tech's info to someone.
    key: 'copy',
    label: 'Copy card details',
    icon: MenuIcons.copy,
    onSelect: async () => {
      const ok = await copyText(vendorAsText(v));
      showToast(ok ? 'Card details copied' : "Couldn't copy — select and copy by hand");
    },
  },
  {
    key: 'copylink',
    label: 'Copy link',
    icon: MenuIcons.link,
    onSelect: async () => {
      const ok = await copyText(vendorLink(v));
      showToast(ok ? 'Link copied — opens this vendor' : "Couldn't copy the link");
    },
  },
  {
    // The native share sheet where the phone has one (iOS Safari, Android
    // Chrome); on a desktop browser without it this quietly becomes a copy.
    key: 'share',
    label: 'Share link',
    icon: MenuIcons.share,
    onSelect: async () => {
      const url = vendorLink(v);
      if (navigator.share) {
        try {
          await navigator.share({ title: v.businessName, url });
          return;
        } catch { return; }   // the user dismissed the sheet — not an error
      }
      const ok = await copyText(url);
      showToast(ok ? 'Link copied' : "Couldn't copy the link");
    },
  },
  {
    // Trello's Watch means "tell me when this changes". The equivalent that
    // matters here: a watched vendor pins to the top of its column, and QPS is
    // copied on that tech's paperwork-expiry reminders.
    key: 'watch',
    label: v.watched ? 'Stop watching' : 'Watch',
    icon: v.watched ? MenuIcons.eyeOff : MenuIcons.eye,
    onSelect: () => onWatch(v),
  },
  {
    key: 'delete',
    label: 'Delete card',
    icon: MenuIcons.trash,
    danger: true,
    onSelect: () => onDelete(v),
  },
];
